import {
  type ColDef,
  type Column,
  type ColumnState,
  type GridApi,
  type GridState,
  type IAggFuncs,
} from "ag-grid-community"

import { CollectionTableColumn, COLLECTION_TABLE_COLUMN_IDS } from "~/constants/collections"
import { persistedAggFuncs } from "~/hooks/useGridState"
import { isCalendarColumn, isStaleColumnId, ProductTableColumn } from "~/utils/productTableUtils"

const baseColumnId = (colId: string) => colId.split("::")[0] ?? colId
export const isCompanionColumn = (colId: string) => colId.includes("::")

export const colIdsOf = (columns: readonly ColDef[]) =>
  columns.map((c) => c.colId ?? "").filter(Boolean)

/**
 * Ids of the columns the column chooser shows and hides: locked-visible, calendar and `::`
 * companion columns are always shown or follow their base, so they never belong in a selection.
 */
export const chooserColumnIdsOf = (columns: readonly ColDef[]) =>
  colIdsOf(columns.filter((c) => !c.lockVisible)).filter(
    (colId) => !isCalendarColumn(colId) && !isCompanionColumn(colId),
  )

type VisibilityInput = {
  isShown: (colId: string, baseColId: string) => boolean
  disabledColumnIdSet: ReadonlySet<string>
  alwaysVisibleColumnIdSet: ReadonlySet<string>
  rowGroupHeaderNames: readonly string[]
}

/**
 * Which colDefs the grid registers, which of those start hidden, which aggregate and which the
 * column chooser shows and hides. Every available column is registered; unselected ones are hidden
 * rather than left out. Company-disabled columns are not registered unless the table needs them
 * anyway (row grouping, always-visible, calendar). Row-group columns are matched by lowercased
 * header name; company-disabled columns never count as shown. A colDef with `hide: true` stays
 * hidden and out of the chooser's reach.
 */
export function resolveColumnVisibility<T extends Pick<ColDef, "colId" | "headerName" | "hide">>(
  colDefs: readonly T[],
  { isShown, disabledColumnIdSet, alwaysVisibleColumnIdSet, rowGroupHeaderNames }: VisibilityInput,
): { colDef: T; hide: boolean; aggregate: boolean; managed: boolean; required: boolean }[] {
  // Columns registered even when company-disabled; "baseId::suffix" follows "baseId".
  const requiredColumnIds = new Set<string>([
    ProductTableColumn.ProductName,
    ProductTableColumn.HideColumns,
  ])
  for (const col of colDefs) {
    if (rowGroupHeaderNames.includes(col.headerName?.toLowerCase() ?? "")) {
      requiredColumnIds.add(col.colId ?? "")
    }
  }
  for (const col of colDefs) {
    const colId = col.colId ?? ""
    if (isCompanionColumn(colId) && requiredColumnIds.has(baseColumnId(colId))) {
      requiredColumnIds.add(colId)
    }
  }

  return colDefs.flatMap((colDef) => {
    const colId = colDef.colId ?? ""
    const baseId = baseColumnId(colId)
    const required = requiredColumnIds.has(colId)
    const alwaysShown = isCalendarColumn(colId) || alwaysVisibleColumnIdSet.has(colId)
    const disabled = disabledColumnIdSet.has(baseId)
    if (disabled && !required && !alwaysShown) return []
    const fixedHidden = colDef.hide === true
    const hide = fixedHidden || (!alwaysShown && (disabled || !isShown(colId, baseId)))
    // Hidden columns stay out of aggregation: every active aggFunc costs a pass over all rows.
    return [
      {
        colDef,
        hide,
        aggregate: !hide || required,
        managed: !required && !alwaysShown && !fixedHidden,
        required,
      },
    ]
  })
}

/**
 * What a product-table colDef carries in `context.visibility`: whether the column chooser shows
 * and hides it (`managed`), whether it aggregates while hidden (`required`), and the aggFunc it
 * aggregates with while it does, which its own `aggFunc` drops while hidden.
 */
export type ColumnVisibility = {
  managed: boolean
  required: boolean
  aggFunc: ColDef["aggFunc"]
}

export const columnVisibilityOf = (colDef: Pick<ColDef, "context"> | undefined) =>
  (colDef?.context as { visibility?: ColumnVisibility } | undefined)?.visibility

/**
 * The registered aggFunc a column with a function aggFunc aggregates through. Column state takes
 * aggFunc names only, so a function by name is what lets one `applyColumnState` turn any set of
 * columns' aggregation on. It runs the function the column's current colDef carries.
 */
export const COLUMN_AGG_FUNC = "productTableColumn"

export const PRODUCT_TABLE_AGG_FUNCS: IAggFuncs = {
  [COLUMN_AGG_FUNC]: (params) => {
    const aggFunc = columnVisibilityOf(params.colDef)?.aggFunc
    return typeof aggFunc === "function" ? aggFunc(params) : undefined
  },
}

/** `aggFunc` as the grid holds it: a name, with a function standing in as {@link COLUMN_AGG_FUNC}. */
export const aggFuncName = (aggFunc: ColDef["aggFunc"]): string | null | undefined =>
  typeof aggFunc === "function" ? COLUMN_AGG_FUNC : aggFunc

/** A registered column as the visibility writers see it. */
export type LiveColumn = {
  colId: string
  visible: boolean
  aggregating: boolean
  aggFunc: ColDef["aggFunc"]
  sorted: boolean
  visibility: ColumnVisibility | undefined
}

// An active aggregation is kept (it may be the user's) unless `override` names another one.
function aggregationChange(
  column: LiveColumn,
  aggregate: boolean,
  aggFunc: string | null | undefined,
  override = false,
): Pick<ColumnState, "aggFunc"> | undefined {
  if (!aggregate) return column.aggregating ? { aggFunc: null } : undefined
  if (aggFunc == null) return undefined
  if (column.aggregating && (!override || column.aggFunc === aggFunc)) return undefined
  return { aggFunc }
}

/**
 * Shows the managed columns in `shownIds` and hides the rest, each with its aggregation turned
 * on or off to match and a hidden column's sort cleared, so a column off screen never orders the
 * rows. `::` companions follow their base id. Unmanaged columns are left alone.
 */
export function visibilityPatch(
  columns: readonly LiveColumn[],
  shownIds: ReadonlySet<string>,
): ColumnState[] {
  return columns.flatMap((column) => {
    const { visibility } = column
    if (!visibility?.managed) return []
    const visible = shownIds.has(baseColumnId(column.colId))
    if (visible === column.visible) return []
    const aggregate = visible || visibility.required
    const change = aggregationChange(column, aggregate, aggFuncName(visibility.aggFunc))
    const sort = !visible && column.sorted ? { sort: null } : {}
    return [{ colId: column.colId, hide: !visible, ...change, ...sort }]
  })
}

/**
 * Turns aggregation on for every visible or required column and off for the rest. An active
 * aggregation is kept, except where `persisted` (a saved extradata aggregation) names another.
 */
export function aggregationPatch(
  columns: readonly LiveColumn[],
  persisted: ReadonlyMap<string, string> = new Map(),
): ColumnState[] {
  return columns.flatMap((column) => {
    const { visibility } = column
    if (!visibility) return []
    const saved = persisted.get(column.colId)
    const change = aggregationChange(
      column,
      column.visible || visibility.required,
      saved ?? aggFuncName(visibility.aggFunc),
      saved != null,
    )
    return change ? [{ colId: column.colId, ...change }] : []
  })
}

/** The managed base columns the grid shows, in grid order. */
export function shownManagedColumnIds(columns: readonly LiveColumn[]): string[] {
  return columns
    .filter((col) => col.visible && col.visibility?.managed && !isCompanionColumn(col.colId))
    .map((col) => col.colId)
}

const liveColumn = (column: Column): LiveColumn => ({
  colId: column.getColId(),
  visible: column.isVisible(),
  aggregating: column.isValueActive(),
  aggFunc: column.getAggFunc(),
  sorted: column.getSort() != null,
  visibility: columnVisibilityOf(column.getColDef()),
})

export const liveColumns = (api: GridApi): LiveColumn[] =>
  api.isDestroyed() ? [] : (api.getColumns() ?? []).map(liveColumn)

function applyPatch(api: GridApi, state: ColumnState[]) {
  if (state.length > 0) api.applyColumnState({ state })
}

/**
 * The one writer of product-table column visibility: shows the managed columns in `ids` and
 * hides the rest in a single column-state pass. Ids the grid hasn't registered are ignored.
 */
export function commitDisplayedColumns(api: GridApi | undefined, ids: readonly string[]) {
  if (!api || api.isDestroyed()) return
  applyPatch(api, visibilityPatch(liveColumns(api), new Set(ids)))
}

/**
 * Brings every product-table column's aggregation in line with its visibility. Run after anything
 * that sets visibility without it: a full state apply, a drag, an outside `applyColumnState`.
 */
export function reconcileAggregation(api: GridApi, persisted?: GridState) {
  if (api.isDestroyed()) return
  applyPatch(api, aggregationPatch(liveColumns(api), persistedAggFuncs(persisted)))
}

// Collection columns a saved view may hold on any table without being flagged invalid.
const ALWAYS_VALID_COLLECTION_COLUMN_IDS: ReadonlySet<string> = new Set([
  CollectionTableColumn.Forecast,
  CollectionTableColumn.Sales,
  CollectionTableColumn.PreOrders,
  CollectionTableColumn.InboundPreOrders,
  CollectionTableColumn.PreOrdersWithoutInbound,
])

const WEEK_COLUMN_IDS: ReadonlySet<string> = new Set(
  Array.from({ length: 64 }, (_, i) => `Week_${i}`),
)

/**
 * The ids a saved view shows that the table no longer has. Calendar, comparison and week columns
 * come and go with the period, gated columns with the context, and collection columns with a
 * collection filter, so none of those count.
 */
export function invalidViewColumnIds(
  viewColumnIds: readonly string[],
  {
    allColumnIds,
    gatedColumnIdSet,
    listCollectionColumnsInChooser,
  }: {
    allColumnIds: readonly string[]
    gatedColumnIdSet: ReadonlySet<string>
    listCollectionColumnsInChooser: boolean
  },
): string[] {
  const available = new Set(allColumnIds)
  const validCollectionColumnIds = listCollectionColumnsInChooser
    ? COLLECTION_TABLE_COLUMN_IDS
    : ALWAYS_VALID_COLLECTION_COLUMN_IDS
  return viewColumnIds.filter(
    (id) =>
      !available.has(id) &&
      !isCalendarColumn(id) &&
      !isStaleColumnId(id) &&
      !id.includes("comparison") &&
      !gatedColumnIdSet.has(id) &&
      !validCollectionColumnIds.has(id) &&
      !WEEK_COLUMN_IDS.has(id),
  )
}
